/* eslint-disable @typescript-eslint/no-explicit-any */
import { FilterField, labelId } from "kontrolEdge/helpers/advancedFilters";
import { SupportTicket } from "./RequestData";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import React from "react";

export const statusText = (s: any): string =>
  typeof s === "string"
    ? s
    : s?.label_for_customers || s?.label || s?.name || "Unknown";

/**
 * The status as one way of writing it: Jira sends some in capitals
 * ("RESOLVED") and others in sentence case ("Canceled") (CTX-1433).
 */
export const displayStatus = (s: any): string => {
  const text = statusText(s);
  const isShouting = text === text.toUpperCase() && /[A-Z]{2}/.test(text);
  return isShouting ? text.charAt(0) + text.slice(1).toLowerCase() : text;
};

/** The three groups a phone filters tickets by. Open includes Waiting for you. */
export const TICKET_PHASES = ["Open", "Waiting for you", "Closed"] as const;
export type TicketPhase = (typeof TICKET_PHASES)[number];

const CLOSED_STATUSES = new Set([
  "resolved",
  "closed",
  "done",
  "canceled",
  "cancelled",
  "rejected",
]);

export const isClosedStatus = (s: any): boolean =>
  CLOSED_STATUSES.has(statusText(s).trim().toLowerCase());

/** The ticket waits for the customer's answer, not for the support team. */
export const isWaitingForCustomer = (s: any): boolean =>
  /^waiting for (customer|you)$/i.test(statusText(s).trim());

export const matchesTicketPhase = (s: any, phase: TicketPhase): boolean => {
  if (phase === "Closed") return isClosedStatus(s);
  if (phase === "Waiting for you") return isWaitingForCustomer(s);
  return !isClosedStatus(s);
};

/** "Incident Report" → "Incident": the type beside the ticket number on a phone. */
export const shortTicketType = (type?: string): string =>
  (type ?? "").replace(/\s+(Request|Report)$/i, "");

/** How long ago, in the largest whole unit: "3 h ago", "2 d ago". */
export const timeAgo = (iso: string | undefined, now = Date.now()): string => {
  const then = iso ? new Date(iso).getTime() : NaN;
  if (!Number.isFinite(then)) return "";
  const minutes = Math.max(0, Math.round((now - then) / 60000));
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days < 60) return `${days} d ago`;
  return `${Math.round(days / 30)} mo ago`;
};

export const mapStatusToVariant = (
  statusRaw: any
): "success" | "neutral" | "error" | "warning" | "default" => {
  const s = statusText(statusRaw).toLowerCase();
  if (s === "resolved") return "success";
  if (s === "rejected") return "error";
  if (s === "pending") return "warning";
  if (s === "canceled") return "default";
  // unknown as neutral
  return "neutral";
};

export const priorityLabel = (
  p?: number
): "Low" | "Medium" | "High" | "Urgent" | "N/A" => {
  switch (Number(p)) {
    case 4:
      return "Low";
    case 3:
      return "Medium";
    case 2:
      return "High";
    case 1:
    case 10000:
      return "Urgent";
    default:
      return "N/A";
  }
};

type Tone = { bg: string; text: string; border?: string };
const TYPE_TONE_MAP: Record<string, Tone> = {
  "Incident Report": { bg: "bg-rose-100", text: "text-rose-800" },
  "Help Request": { bg: "bg-purple-100", text: "text-purple-800" },
  "Change Request": { bg: "bg-indigo-100", text: "text-indigo-800" },
  "Feature Request": { bg: "bg-pink-100", text: "text-pink-800" },
  "Incident Request": { bg: "bg-sky-100", text: "text-sky-800" },
  __default: { bg: "bg-gray-200", text: "text-gray-800" },
};

export const typeTone = (t?: string): Tone =>
  TYPE_TONE_MAP[t ?? ""] ?? TYPE_TONE_MAP.__default;
export const TicketTypeTag: React.FC<{ value?: string; compact?: boolean }> = ({
  value,
  compact = true,
}) => {
  const { bg, text, border } = typeTone(value);
  const sizing = compact ? "text-xs px-2 py-0.5" : "text-sm px-2.5 py-1";
  return (
    <span
      className={[
        "inline-flex items-center font-semibold rounded",
        bg,
        text,
        border ?? "",
        sizing,
      ]
        .join(" ")
        .trim()}
    >
      {value ?? "N/A"}
    </span>
  );
};

export const mapTypeToVariant = (
  t?: string
): "success" | "warning" | "error" | "neutral" => {
  switch (t) {
    case "Incident Report":
      return "error";
    case "Help Request":
      return "warning";
    case "Change Request":
      return "neutral";
    case "Feature Request":
      return "success";
    default:
      return "neutral";
  }
};

export const mapPriorityToVariant = (
  p?: number
): "success" | "warning" | "error" | "default" => {
  const label = priorityLabel(p);
  if (label === "Low") return "success";
  if (label === "Medium") return "warning";
  if (label === "High" || label === "Urgent") return "error";
  return "default";
};
// export const TicketTypeTag: React.FC<{ value?: string }> = ({ value }) => {
//   const variant = mapTypeToVariant(value);
//   return <GenericTag variant={variant} label={value ?? "N/A"} size="small" />;
// };

// GenericTag's `default` variant is raw Tailwind gray, which stays light in
// dark mode. Support's neutral tags (Canceled, N/A) use semantic tokens
// instead (CTX-1121).
const neutralTagColors = (variant: string) =>
  variant === "default"
    ? {
        backgroundColor: "bg-semantic-bg-tertiary",
        textColor: "text-semantic-text-secondary",
      }
    : {};

// Optional tiny render helpers (use GenericTag)
export const TicketStatusTag: React.FC<{ value: any }> = ({ value }) => {
  const text = displayStatus(value);
  const variant = mapStatusToVariant(value);
  return (
    <GenericTag
      variant={variant}
      label={text}
      size="small"
      {...neutralTagColors(variant)}
    />
  );
};

export const TicketPriorityTag: React.FC<{ value?: number }> = ({ value }) => {
  const label = priorityLabel(value);
  const variant = mapPriorityToVariant(value);
  return (
    <GenericTag
      variant={variant}
      label={label}
      size="small"
      {...neutralTagColors(variant)}
    />
  );
};

export function makeSupportFields(
  isViewingAllCustomers: boolean,
  isKontrolEdge?: boolean
): FilterField<SupportTicket>[] {
  const fields: FilterField<SupportTicket>[] = [
    {
      label: "Type",
      key: labelId("Type"),
      accessor: (r) => r.type ?? "",
      type: "string",
      inFreeText: true,
    },
    {
      // Works across products: match either key or id
      label: "Ticket Number",
      key: labelId("Ticket Number"),
      accessor: (r) => `${r.key ?? ""} ${r.id ?? ""}`.trim(),
      type: "string",
      inFreeText: true,
    },
    {
      label: "Title",
      key: "subject",
      accessor: (r) => r.subject,
      type: "string",
      inFreeText: true,
    },
    { label: "Reporter",
      key: "reporter",
      accessor: (r) => r.reporterEmail,
      type: "string",
      inFreeText: true
    },
    {
      label: "Status",
      key: labelId("Status"),
      accessor: (r) => statusText(r.status),
      type: "string",
      inFreeText: true,
    },
    {
      // Filter by priority label (High, Medium, Low, Urgent) instead of number
      label: "Priority",
      key: labelId("Priority"),
      accessor: (r) => priorityLabel(r.priority),
      type: "string",
      inFreeText: true,
    },
    {
      label: "Last update",
      key: labelId("Last update"),
      accessor: (r) => {
        if (!r.updatedAt) return "";
        // Extract date part from ISO string (e.g., "2026-02-16T18:05:13.214-0600" -> "2026-02-16")
        const datePart = r.updatedAt.split("T")[0];
        return datePart || "";
      },
      type: "string",
      inFreeText: true,
    }
  ];

  if (isViewingAllCustomers) {
    fields.splice(1, 0, {
      label: "Customer",
      key: "customer",
      accessor: (r) =>
        [r.customer?.name ?? "", r.customer?.chargebeeCustomerId ?? ""]
          .filter(Boolean)
          .join(" "),
      type: "string",
      inFreeText: true,
    });
  }
  
  // Add Service ID field for Kontrol Edge
  if (isKontrolEdge) {
    fields.push({
      label: "Service ID",
      key: labelId("Service ID"),
      accessor: (r) => {
        // Handle service_id as string, array, or null/undefined
        if (!r.service_id) return "";
        if (Array.isArray(r.service_id)) {
          return r.service_id.join(" ");
        }
        if (typeof r.service_id === "string") {
          return r.service_id.replace(/,/g, " ");
        }
        return "";
      },
      type: "string",
      inFreeText: true,
    });
  }

  for (const f of fields) f.key ||= labelId(f.label);
  return fields;
}
